import React, { useState, useEffect } from 'react';
import { PolicyRevision } from '../../types';
import { GitCompare, ArrowRight } from 'lucide-react';

interface VersionCompareSelectorProps {
  revisions: PolicyRevision[];
  onCompare: (baseRevisionId: string, targetRevisionId: string) => void;
  currentBaseId?: string | null;
  currentTargetId?: string | null;
}

export const VersionCompareSelector: React.FC<VersionCompareSelectorProps> = ({
  revisions,
  onCompare,
  currentBaseId,
  currentTargetId,
}) => {
  const [baseId, setBaseId] = useState<string>(() => {
    if (currentBaseId) return currentBaseId;
    return revisions.length >= 2 ? revisions[1].id : (revisions[0]?.id || '');
  });

  const [targetId, setTargetId] = useState<string>(() => {
    if (currentTargetId) return currentTargetId;
    return revisions[0]?.id || '';
  });

  useEffect(() => {
    if (currentBaseId) setBaseId(currentBaseId);
    if (currentTargetId) setTargetId(currentTargetId);
  }, [currentBaseId, currentTargetId]);

  if (revisions.length < 2) return null;

  const handleCompareClick = () => {
    if (baseId && targetId && baseId !== targetId) {
      onCompare(baseId, targetId);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700/80 shadow-2xs flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
      <div className="flex items-center gap-2">
        <div className="p-2 rounded-lg bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400">
          <GitCompare className="w-4 h-4" />
        </div>
        <div>
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
            Compare Arbitrary Revisions
          </h4>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">
            Select any two versions to compute instant word and line diffs
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2.5">
        {/* Base Version Selector */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-medium text-slate-500">Base (Old):</span>
          <select
            value={baseId}
            onChange={(e) => setBaseId(e.target.value)}
            className="bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono"
          >
            {revisions.map((r) => (
              <option key={`base-${r.id}`} value={r.id}>
                {r.title || r.id}
              </option>
            ))}
          </select>
        </div>

        <ArrowRight className="w-4 h-4 text-slate-400 hidden sm:inline" />

        {/* Target Version Selector */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-medium text-slate-500">Target (New):</span>
          <select
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
            className="bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono"
          >
            {revisions.map((r) => (
              <option key={`target-${r.id}`} value={r.id}>
                {r.title || r.id}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={handleCompareClick}
          disabled={baseId === targetId}
          className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors shadow-2xs"
        >
          <GitCompare className="w-3.5 h-3.5" />
          Compare
        </button>
      </div>
    </div>
  );
};

export const VersionSelector = VersionCompareSelector;
export default VersionCompareSelector;
